import { useState } from 'react';
import { User, Save, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export default function MyProfile() {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [passwordErrorMsg, setPasswordErrorMsg] = useState('');

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) {
      setPasswordErrorMsg('A senha deve ter pelo menos 6 caracteres.');
      setPasswordStatus('error');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordErrorMsg('As senhas não coincidem.');
      setPasswordStatus('error');
      return;
    }

    setSavingPassword(true);
    setPasswordStatus('idle');

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setPasswordErrorMsg('Erro ao alterar a senha. Tente novamente.');
      setPasswordStatus('error');
    } else {
      setPasswordStatus('success');
      setNewPassword('');
      setConfirmPassword('');
    }

    setSavingPassword(false);
  };

  const handleSave = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setErrorMsg('O nome não pode estar vazio.');
      setStatus('error');
      return;
    }
    if (!user) return;

    setSaving(true);
    setStatus('idle');

    const { error } = await supabase
      .from('users')
      .update({ display_name: trimmed })
      .eq('id', user.id);

    if (error) {
      setErrorMsg('Erro ao salvar. Tente novamente.');
      setStatus('error');
    } else {
      await refreshUser();
      setStatus('success');
    }

    setSaving(false);
  };

  const initials = displayName.trim()
    ? displayName.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : user?.name?.slice(0, 2).toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure as informações exibidas nos seus links de cadastro.
          </p>
        </div>

        {/* Avatar preview */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
            <span className="text-white text-xl font-semibold tracking-wide">{initials}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-lg leading-tight">
              {displayName.trim() || user?.name || '—'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Pré-visualização do nome exibido</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Nome de exibição
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5" htmlFor="display-name">
                Nome que aparecerá no link de cadastro enviado aos clientes
              </label>
              <input
                id="display-name"
                type="text"
                value={displayName}
                onChange={e => {
                  setDisplayName(e.target.value);
                  setStatus('idle');
                }}
                placeholder="Ex: João Silva"
                maxLength={60}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-900 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  transition-shadow placeholder-gray-400"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {displayName.length}/60 caracteres
              </p>
            </div>

            {/* Feedback */}
            {status === 'success' && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3 text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                Nome atualizado com sucesso.
              </div>
            )}
            {status === 'error' && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errorMsg}
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700
                disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition-colors text-sm"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-2xl p-4">
          <p className="text-xs text-blue-700 leading-relaxed">
            <span className="font-semibold">Como funciona:</span> ao enviar um link de cadastro a um cliente,
            o nome definido aqui será exibido na mensagem de boas-vindas. Se nenhum nome for definido,
            será usado o prefixo do seu e-mail.
          </p>
        </div>

        {/* Password change card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Alterar senha
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5" htmlFor="new-password">
                Nova senha
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => {
                    setNewPassword(e.target.value);
                    setPasswordStatus('idle');
                  }}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 text-gray-900 text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    transition-shadow placeholder-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1.5" htmlFor="confirm-password">
                Confirmar nova senha
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => {
                    setConfirmPassword(e.target.value);
                    setPasswordStatus('idle');
                  }}
                  placeholder="Repita a nova senha"
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 text-gray-900 text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    transition-shadow placeholder-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {passwordStatus === 'success' && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3 text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                Senha alterada com sucesso.
              </div>
            )}
            {passwordStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {passwordErrorMsg}
              </div>
            )}

            <button
              onClick={handlePasswordChange}
              disabled={savingPassword || !newPassword || !confirmPassword}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700
                disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition-colors text-sm"
            >
              <Lock className="w-4 h-4" />
              {savingPassword ? 'Alterando...' : 'Alterar senha'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
